/**
 * GymLog: local mutations (PowerSync via Drizzle).
 *
 * Writes to SQLite; PowerSync uploads changes to the server in the background
 * (offline queue). Same pattern as `todos/mutations.ts` from template.
 */

import { and, eq } from "drizzle-orm";
import {
  body_measurement,
  type BodyMeasurementInsert,
  exercise,
  type PowerSyncDrizzleDB,
  routine,
  routine_exercise,
  workout_session,
  workout_set,
} from "@gymlog/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
} from "../powersync/verified-write";
import type { EquipmentType, MuscleGroup } from "./constants";

// ─── Custom exercises ────────────────────────────────────────────────────────

export async function createExerciseMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: {
    name: string;
    muscleGroup: MuscleGroup;
    equipment: EquipmentType;
    defaultRestSeconds?: number | null;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  const name = input.name.trim();
  await db.insert(exercise).values({
    id,
    user_id: userId,
    // Custom exercise: user name applies in both languages.
    name_en: name,
    name_es: name,
    muscle_group: input.muscleGroup,
    equipment: input.equipment,
    default_rest_seconds: input.defaultRestSeconds ?? null,
    is_archived: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateExerciseMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
  updates: Partial<{
    name: string;
    muscleGroup: MuscleGroup;
    equipment: EquipmentType;
    defaultRestSeconds: number | null;
    isArchived: boolean;
  }>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const set: Record<string, unknown> = {};
  if (updates.name !== undefined) {
    const name = updates.name.trim();
    set.name_en = name;
    set.name_es = name;
  }
  if (updates.muscleGroup !== undefined) {
    set.muscle_group = updates.muscleGroup;
  }
  if (updates.equipment !== undefined) {
    set.equipment = updates.equipment;
  }
  if (updates.defaultRestSeconds !== undefined) {
    set.default_rest_seconds = updates.defaultRestSeconds;
  }
  if (updates.isArchived !== undefined) {
    set.is_archived = updates.isArchived ? 1 : 0;
  }
  // Only custom exercises: global catalog is read-only —
  // `user_id` serves as `scope` (applied also to re-read), not just to the
  // UPDATE (§4.2 of guide).
  await patchRowVerified(db, exercise, exercise.id, id, set, {
    ...options,
    scope: [{ column: exercise.user_id, value: userId }],
  });
}

/**
 * Trash (L2): deleting a custom exercise from UI = soft-delete. The
 * exercise disappears from catalog, routines, and weekly summary
 * (queries filter `deleted_at IS NULL`) but its sets/history are
 * PRESERVED intact and reappear upon restore. Only custom exercises: the
 * global catalog (user_id NULL) is read-only.
 */
export async function trashExerciseMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    exercise,
    exercise.id,
    id,
    { deleted_at: nowISO() },
    { ...options, scope: [{ column: exercise.user_id, value: userId }] },
  );
}

/** Restore from trash: exercise returns with all its history. */
export async function restoreExerciseMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    exercise,
    exercise.id,
    id,
    { deleted_at: null },
    { ...options, scope: [{ column: exercise.user_id, value: userId }] },
  );
}

/**
 * Hard DELETE of a custom exercise (from trash, or inverse of
 * "create exercise"): cascades its sets. Global catalog is untouchable.
 */
export async function deleteExerciseMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  id: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(workout_set).where(eq(workout_set.exercise_id, id));
    await trx.delete(exercise).where(and(eq(exercise.id, id), eq(exercise.user_id, userId)));
  });
}

// ─── Routines ──────────────────────────────────────────────────────────────────

export async function createRoutineMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: { name: string; description?: string | null; position?: number },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(routine).values({
    id,
    user_id: userId,
    name: input.name.trim(),
    description: input.description ?? null,
    position: input.position ?? 0,
    is_archived: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateRoutineMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    description: string | null;
    position: number;
  }>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const set: Record<string, unknown> = {};
  if (updates.name !== undefined) {
    set.name = updates.name.trim();
  }
  if (updates.description !== undefined) {
    set.description = updates.description;
  }
  if (updates.position !== undefined) {
    set.position = updates.position;
  }
  await patchRowVerified(db, routine, routine.id, id, set, options);
}

/**
 * Trash (L2): deleting a routine from UI = soft-delete. Disappears from
 * list (queries filter `deleted_at IS NULL`) but its routine_exercise rows are
 * PRESERVED: restoring brings it back complete. Past sessions are NOT touched.
 */
export async function trashRoutineMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, routine, routine.id, id, { deleted_at: nowISO() }, options);
}

/** Restore from trash: routine returns with its exercises. */
export async function restoreRoutineMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, routine, routine.id, id, { deleted_at: null }, options);
}

/**
 * Hard DELETE of the routine and its exercises (from trash, or
 * inverse of "create routine"). Past sessions are NOT touched.
 */
export async function deleteRoutineMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(routine_exercise).where(eq(routine_exercise.routine_id, id));
    await trx.delete(routine).where(eq(routine.id, id));
  });
}

// ─── ROUTINE VERSIONING: restore a version (forward write) ────────────────

/** An item as serialized in `routine_version.items`. */
interface VersionItemNode {
  exercise_id: string;
  position: number;
  target_sets: number | null;
  target_reps_min: number | null;
  target_reps_max: number | null;
  rest_seconds: number | null;
  note: string | null;
}

/** Minimal snapshot necessary to restore (from local bucket or API). */
export interface RoutineVersionSnapshot {
  name: string | null;
  description: string | null;
  /** `items` may arrive as JSON string (local bucket) or parsed array (API). */
  items: string | unknown[] | null;
}

function asNumberOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function asStringOrNull(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

/** Parses `routine_version.items` (tolerant) into normalized nodes. Discards
 *  items without `exercise_id` (not reinsertable due to NOT NULL FK). */
export function parseVersionItems(items: string | unknown[] | null): VersionItemNode[] {
  let raw: unknown = items;
  if (typeof items === "string") {
    try {
      raw = JSON.parse(items);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((x): x is Record<string, unknown> => Boolean(x) && typeof x === "object")
    .map((node, index) => ({
      exercise_id: asStringOrNull(node.exercise_id) ?? "",
      position: asNumberOrNull(node.position) ?? index,
      target_sets: asNumberOrNull(node.target_sets),
      target_reps_min: asNumberOrNull(node.target_reps_min),
      target_reps_max: asNumberOrNull(node.target_reps_max),
      rest_seconds: asNumberOrNull(node.rest_seconds),
      note: asStringOrNull(node.note),
    }))
    .filter((node) => node.exercise_id !== "");
}

/**
 * Restores a version from history (ROUTINE VERSIONING): NORMAL forward write
 * in ONE transaction (name/description + REPLACEMENT of the routine_exercise
 * set to match snapshot). Never rewrites history: server write-path
 * materializes restored content as a NEW version (current), and existing
 * session seals DO NOT change. DOES NOT touch routine is_archived/deleted_at/position.
 *
 * Must run inside `db.transaction` so PowerSync uploads the gesture as ONE batch
 * (a single new version). routine_exercise rows are recreated with new ids
 * (snapshot does not store stable ids) — known limitation of restore by snapshot.
 */
export async function restoreRoutineVersionMutation(
  db: PowerSyncDrizzleDB,
  routineId: string,
  userId: string,
  version: RoutineVersionSnapshot,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const nodes = parseVersionItems(version.items).toSorted((a, b) => a.position - b.position);
  // Inside a `db.transaction` an UPDATE cannot be retried (it would sleep
  // while holding the write lock): wait until routine is present in view
  // BEFORE opening transaction (same criterion as
  // `swapRoutineExercisesMutation`, see `verified-write.ts`).
  await waitForRowPresent(db, routine, routine.id, routineId, options);
  await db.transaction(async (trx) => {
    await trx
      .update(routine)
      .set({
        name: version.name ?? "",
        description: version.description ?? null,
        updated_at: nowISO(),
      })
      .where(eq(routine.id, routineId));
    // Replace the set of exercises to match the snapshot exactly.
    await trx.delete(routine_exercise).where(eq(routine_exercise.routine_id, routineId));
    const now = nowISO();
    for (const node of nodes) {
      await trx.insert(routine_exercise).values({
        id: generateId(),
        routine_id: routineId,
        exercise_id: node.exercise_id,
        user_id: userId,
        position: node.position,
        target_sets: node.target_sets,
        target_reps_min: node.target_reps_min,
        target_reps_max: node.target_reps_max,
        rest_seconds: node.rest_seconds,
        note: node.note,
        created_at: now,
        updated_at: now,
      });
    }
  });
}

export async function addExerciseToRoutineMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: {
    routineId: string;
    exerciseId: string;
    position: number;
    targetSets?: number | null;
    targetRepsMin?: number | null;
    targetRepsMax?: number | null;
    restSeconds?: number | null;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(routine_exercise).values({
    id,
    routine_id: input.routineId,
    exercise_id: input.exerciseId,
    user_id: userId,
    position: input.position,
    target_sets: input.targetSets ?? null,
    target_reps_min: input.targetRepsMin ?? null,
    target_reps_max: input.targetRepsMax ?? null,
    rest_seconds: input.restSeconds ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateRoutineExerciseMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    position: number;
    targetSets: number | null;
    targetRepsMin: number | null;
    targetRepsMax: number | null;
    restSeconds: number | null;
    note: string | null;
  }>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const set: Record<string, unknown> = {};
  if (updates.position !== undefined) {
    set.position = updates.position;
  }
  if (updates.targetSets !== undefined) {
    set.target_sets = updates.targetSets;
  }
  if (updates.targetRepsMin !== undefined) {
    set.target_reps_min = updates.targetRepsMin;
  }
  if (updates.targetRepsMax !== undefined) {
    set.target_reps_max = updates.targetRepsMax;
  }
  if (updates.restSeconds !== undefined) {
    set.rest_seconds = updates.restSeconds;
  }
  if (updates.note !== undefined) {
    set.note = updates.note;
  }
  await patchRowVerified(db, routine_exercise, routine_exercise.id, id, set, options);
}

export async function removeRoutineExerciseMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(routine_exercise).where(eq(routine_exercise.id, id));
}

/** Swaps the position of two routine exercises (reorder ↑/↓). */
export async function swapRoutineExercisesMutation(
  db: PowerSyncDrizzleDB,
  a: { id: string; position: number },
  b: { id: string; position: number },
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const now = nowISO();
  // Cannot retry inside transaction: wait until BOTH rows are present
  // in view before opening tx, in PARALLEL (a row that did not need to
  // wait does not pay the backoff of the other).
  await Promise.all([
    waitForRowPresent(db, routine_exercise, routine_exercise.id, a.id, options),
    waitForRowPresent(db, routine_exercise, routine_exercise.id, b.id, options),
  ]);
  await db.transaction(async (trx) => {
    await trx
      .update(routine_exercise)
      .set({ position: b.position, updated_at: now })
      .where(eq(routine_exercise.id, a.id));
    await trx
      .update(routine_exercise)
      .set({ position: a.position, updated_at: now })
      .where(eq(routine_exercise.id, b.id));
  });
}

// ─── Sessions ─────────────────────────────────────────────────────────────────

export async function startSessionMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: { name: string; routineId?: string | null },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(workout_session).values({
    id,
    user_id: userId,
    routine_id: input.routineId ?? null,
    name: input.name.trim(),
    started_at: now,
    finished_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateSessionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ name: string; note: string | null }>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const set: Record<string, unknown> = {};
  if (updates.name !== undefined) {
    set.name = updates.name.trim();
  }
  if (updates.note !== undefined) {
    set.note = updates.note;
  }
  await patchRowVerified(db, workout_session, workout_session.id, id, set, options);
}

export async function finishSessionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  note?: string | null,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const set: Record<string, unknown> = { finished_at: nowISO() };
  if (note !== undefined) {
    set.note = note;
  }
  await patchRowVerified(db, workout_session, workout_session.id, id, set, options);
}

/** Deletes the session and all its sets. */
export async function deleteSessionMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(workout_set).where(eq(workout_set.session_id, id));
    await trx.delete(workout_session).where(eq(workout_session.id, id));
  });
}

/**
 * Snapshot of a session + its sets for undoing deletion (L1). Sessions
 * DO NOT have a trash bin (L2): deletion is direct and safety net
 * is immediate undo, which re-inserts identical session and sets.
 */
export interface WorkoutSessionSnapshot {
  session: {
    id: string;
    routineId: string | null;
    name: string;
    startedAt: string | null;
    finishedAt: string | null;
    note: string | null;
  };
  sets: WorkoutSetSnapshot[];
}

/** Inverse of `deleteSessionMutation`: re-inserts session and all its sets. */
export async function restoreSessionMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  snap: WorkoutSessionSnapshot,
): Promise<void> {
  const now = nowISO();
  await db.transaction(async (trx) => {
    await trx.insert(workout_session).values({
      id: snap.session.id,
      user_id: userId,
      routine_id: snap.session.routineId,
      name: snap.session.name,
      started_at: snap.session.startedAt ?? now,
      finished_at: snap.session.finishedAt,
      note: snap.session.note,
      created_at: now,
      updated_at: now,
    });
    for (const s of snap.sets) {
      await trx.insert(workout_set).values({
        id: s.id,
        session_id: s.sessionId,
        exercise_id: s.exerciseId,
        user_id: userId,
        set_number: s.setNumber,
        weight_kg: s.weightKg,
        reps: s.reps,
        rpe: s.rpe,
        is_warmup: s.isWarmup ?? 0,
        completed_at: s.completedAt ?? now,
        created_at: now,
        updated_at: now,
      });
    }
  });
}

// ─── Sets ─────────────────────────────────────────────────────────────────────

export async function logSetMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: {
    sessionId: string;
    exerciseId: string;
    setNumber: number;
    weightKg: number;
    reps: number;
    rpe?: number | null;
    isWarmup?: boolean;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(workout_set).values({
    id,
    session_id: input.sessionId,
    exercise_id: input.exerciseId,
    user_id: userId,
    set_number: input.setNumber,
    weight_kg: input.weightKg,
    reps: input.reps,
    rpe: input.rpe ?? null,
    is_warmup: input.isWarmup ? 1 : 0,
    completed_at: now,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateSetMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    weightKg: number;
    reps: number;
    rpe: number | null;
    isWarmup: boolean;
  }>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const set: Record<string, unknown> = {};
  if (updates.weightKg !== undefined) {
    set.weight_kg = updates.weightKg;
  }
  if (updates.reps !== undefined) {
    set.reps = updates.reps;
  }
  if (updates.rpe !== undefined) {
    set.rpe = updates.rpe;
  }
  if (updates.isWarmup !== undefined) {
    set.is_warmup = updates.isWarmup ? 1 : 0;
  }
  await patchRowVerified(db, workout_set, workout_set.id, id, set, options);
}

export async function deleteSetMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(workout_set).where(eq(workout_set.id, id));
}

/**
 * Snapshot of a set for undoing deletion (L1): what is needed to
 * re-insert it identically (same id/number/timestamp) via write-path.
 */
export interface WorkoutSetSnapshot {
  id: string;
  sessionId: string;
  exerciseId: string;
  setNumber: number | null;
  weightKg: number | null;
  reps: number | null;
  rpe: number | null;
  isWarmup: number | null;
  completedAt: string | null;
}

/** Inverse of `deleteSetMutation`: re-inserts the set as it was. */
export async function restoreSetMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  snap: WorkoutSetSnapshot,
): Promise<void> {
  const now = nowISO();
  await db.insert(workout_set).values({
    id: snap.id,
    session_id: snap.sessionId,
    exercise_id: snap.exerciseId,
    user_id: userId,
    set_number: snap.setNumber,
    weight_kg: snap.weightKg,
    reps: snap.reps,
    rpe: snap.rpe,
    is_warmup: snap.isWarmup ?? 0,
    completed_at: snap.completedAt ?? now,
    created_at: now,
    updated_at: now,
  });
}

// ─── Medidas corporales ──────────────────────────────────────────────────────

export async function addBodyMeasurementMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  input: Omit<BodyMeasurementInsert, "id" | "user_id" | "created_at" | "updated_at">,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(body_measurement).values({
    ...input,
    id,
    user_id: userId,
    measured_at: input.measured_at ?? now,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateBodyMeasurementMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<Omit<BodyMeasurementInsert, "id" | "user_id" | "created_at" | "updated_at">>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(db, body_measurement, body_measurement.id, id, { ...updates }, options);
}

export async function deleteBodyMeasurementMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(body_measurement).where(eq(body_measurement.id, id));
}
